import React, { useContext, useEffect, useRef } from "react";
import { AppContext } from "../App";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import { FiLayers, FiCpu, FiUsers, FiTrendingUp, FiActivity, FiSearch } from "react-icons/fi"; 
import banner1 from "../assets/banner2.jpg"; 
import banner2 from "../assets/banner1.jpg";

export default function HomeHero() {
  const appContext = useContext(AppContext);
  const sectionRef = useRef(null);

  // Scroll Reveal Logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("w-reveal-active");
          }
        });
      },
      { threshold: 0.1 }
    );

    const revealElements = sectionRef.current.querySelectorAll(".w-reveal");
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const wereniumFeatures = [
    { title: "Business Growth", icon: <FiTrendingUp />, label: "Grow Your Business" },
    { title: "Big Opportunity", icon: <FiActivity />, label: "Growth Catalyst" },
    { title: "Digital Era", icon: <FiSearch />, label: "Modern Solutions" }
  ];

  return (
    <>
      <style>{`
        /* ── Base Section Logic ── */
        .w-hero-viewport {
          position: relative;
          width: 100%;
          min-height: 100vh;
          display: flex;
          align-items: center;
          background-color: var(--bg-dark, #0a1118);
          overflow: hidden;
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* ── Transitions & Reveals ── */
        .w-reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 1, 0.3, 1);
        }

        .w-reveal-active {
          opacity: 1;
          transform: translateY(0);
        }

        .delay-1 { transition-delay: 0.1s; }
        .delay-2 { transition-delay: 0.2s; }
        .delay-3 { transition-delay: 0.3s; }
        .delay-4 { transition-delay: 0.4s; }

        /* ── High-Tech Overlays ── */
        .w-grid-overlay {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(22, 50, 79, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(22, 50, 79, 0.05) 1px, transparent 1px);
          background-size: clamp(30px, 5vw, 50px) clamp(30px, 5vw, 50px);
          z-index: 1;
        }

        .w-glow-accent {
          position: absolute;
          width: clamp(300px, 60vw, 600px);
          height: clamp(300px, 60vw, 600px);
          background: radial-gradient(circle, rgba(22, 50, 79, 0.2) 0%, rgba(22, 50, 79, 0) 70%);
          border-radius: 50%;
          z-index: 2;
        }

        .accent-top { top: -15%; left: -10%; }
        .accent-bottom { bottom: -15%; right: -10%; }

        .w-hero-container {
          position: relative;
          z-index: 3;
          width: 100%;
          max-width: 1300px;
          margin: 0 auto;
          padding: clamp(60px, 10vh, 100px) 25px;
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: clamp(40px, 8vw, 80px);
          align-items: center;
        }

        /* ── Left Content (Text) ── */
        .w-welcome-tag {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 8px 16px;
          background: rgba(22, 50, 79, 0.1);
          color: var(--lightColor);
          font-weight: 700;
          font-size: 0.75rem;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 25px;
        }

        .w-welcome-tag::before {
          content: "";
          width: 8px;
          height: 8px;
          background-color: var(--secondaryColor);
          border-radius: 50%;
          animation: w-pulse 1.5s infinite;
        }

        @keyframes w-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        .w-hero-title {
          color: #ffffff;
          font-size: clamp(2.4rem, 6vw, 4.8rem);
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: -0.05em;
          margin-bottom: 25px;
        }

        .w-hero-title span {
          display: block;
          color: var(--text-muted, #94a3b8);
          font-size: clamp(1.4rem, 3.5vw, 2.8rem);
          font-weight: 500;
          letter-spacing: -0.04em;
          margin-top: 10px;
        }

        .w-hero-subtitle {
          color: var(--text-muted, #94a3b8);
          font-size: clamp(1rem, 2vw, 1.2rem);
          line-height: 1.6;
          max-width: 540px;
          margin-bottom: 40px;
          font-weight: 400;
        }

        .w-action-row {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }

        .w-btn {
          padding: 16px 32px;
          border-radius: 4px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-size: 0.75rem;
          transition: 0.3s cubic-bezier(0.23, 1, 0.32, 1);
          display: inline-flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          text-decoration: none;
        }

        .w-btn-primary {
          background: var(--secondaryColor);
          color: #ffffff;
          border: none;
        }

        .w-btn-primary:hover {
          transform: translateY(-4px);
          filter: brightness(1.1);
          box-shadow: 0 15px 30px rgba(13, 14, 15, 0.3);
        }

        .w-btn-ghost {
          background: transparent;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        /* ── Right Content (Images) ── */
        .w-visual-stack {
          position: relative;
          height: clamp(350px, 50vh, 600px);
          width: 100%;
        }

        .w-img-frame {
          position: absolute;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(22, 50, 79, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
          transition: all 0.4s ease;
        }

        .w-img-frame img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .frame-main {
          top: 0;
          right: 0;
          width: 85%;
          height: 80%;
          z-index: 2;
        }

        .frame-sub {
          bottom: 5%;
          left: 0;
          width: 60%;
          height: 50%;
          z-index: 3;
          border: clamp(4px, 1vw, 8px) solid var(--bg-dark, #0a1118);
        }

        /* ── Bottom Feature Bar ── */
        .w-feature-bar {
          background-color: rgba(22, 50, 79, 0.03);
          border-top: 1px solid rgba(22, 50, 79, 0.1);
          padding: clamp(40px, 8vw, 60px) 0;
        }

        .w-feature-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(20px, 4vw, 40px);
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 25px;
        }

        .w-feature-card {
          display: flex;
          align-items: center;
          gap: 15px;
          color: #ffffff;
        }

        .w-feat-icon {
          width: clamp(44px, 5vw, 56px);
          height: clamp(44px, 5vw, 56px);
          background: var(--secondaryColor);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: clamp(1.2rem, 2vw, 1.6rem);
          flex-shrink: 0;
        }

        .w-feat-title {
          font-size: clamp(0.95rem, 1.5vw, 1.15rem);
          font-weight: 800;
          margin-bottom: 2px;
        }

        .w-feat-label {
          color: var(--text-muted, #94a3b8);
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* ── MOBILE FRIENDLY BREAKPOINTS ── */
        @media (max-width: 991px) {
          .w-hero-container {
            grid-template-columns: 1fr;
            text-align: center;
            gap: 60px;
          }
          .w-content-left {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .w-hero-subtitle {
            margin-left: auto;
            margin-right: auto;
          }
          .w-action-row {
            justify-content: center;
          }
          .w-feature-grid {
            grid-template-columns: 1fr;
            max-width: 400px;
            margin: 0 auto;
            gap: 30px;
          }
          .frame-main { width: 100%; right: 0; }
          .frame-sub { left: 0; width: 55%; }
        }

        @media (max-width: 480px) {
          .w-hero-container { padding: 80px 20px; }
          .w-btn { width: 100%; justify-content: center; }
          .w-visual-stack { height: 300px; }
          .w-feature-bar { padding: 40px 15px; }
        }
      `}</style>

      <section className="w-hero-viewport" ref={sectionRef}>
        <div className="w-grid-overlay" />
        <div className="w-glow-accent accent-top" />
        <div className="w-glow-accent accent-bottom" />

        <div className="w-hero-container">
          {/* Left Column: Focused Text */}
          <div className="w-content-left">
            <div className="w-welcome-tag w-reveal">
              Welcome to Werenium
            </div>

            <h1 className="w-hero-title text-secondary w-reveal delay-1">
              Growing Business. <br />
              <span>In this Digital Era.</span>
            </h1>

            <p className="w-hero-subtitle w-reveal delay-2">
              A Big Opportunity for your Business Growth. Werenium provides 
              high-fidelity software solutions engineered to scale your enterprise.
            </p>

            <div className="w-action-row w-reveal delay-3">
              <button className="w-btn w-btn-primary" onClick={() => appContext.navTo({ item: "services" })}>
                Explore Solutions <HiOutlineArrowNarrowRight size={20} />
              </button>
              <button className="w-btn w-btn-ghost" onClick={() => appContext.navTo({ item: "contact_us" })}>
                Contact Us
              </button>
            </div>
          </div>

          {/* Right Column: Visual Stack */}
          <div className="w-visual-stack w-reveal delay-4">
            <div className="w-img-frame frame-main">
              <img src={banner1} alt="Werenium Core Technology" loading="lazy" />
            </div>
            <div className="w-img-frame frame-sub">
              <img src={banner2} alt="Collaborative Innovation" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

    </>
  );
}